import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle2, ShieldAlert, ShieldCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErpTable, MetricCard, Td, Th } from '@/components/erp/primitives'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { fmtNumber } from '@/shared/lib/format'
import { useCan } from '@/shared/auth/session'
import {
  vaultDrift, vaultInventory, type DriftReport, type PiiFieldResponse, type VaultInventory,
} from './api'

/**
 * What personal data this system holds — and whether it is protected as claimed.
 *
 * ## The register is checked, not trusted
 *
 * The version this was ported from was a hand-written list nobody verified, which is how
 * protection rots: a column is added, the list is not, and the document keeps saying the
 * data is encrypted long after that stopped being true. Every row here carries a verified
 * mark from a check against the live schema.
 *
 * ## Counts of rows, never values
 *
 * "How many voters have a phone on record" is a figure a data-protection officer needs and
 * a strategist cannot misuse. Not one personal value reaches this page.
 */
export function VaultPage() {
  const [inventory, setInventory] = useState<VaultInventory | null>(null)
  const [drift, setDrift] = useState<DriftReport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [filterClass, setFilterClass] = useState('')
  const [filterProtection, setFilterProtection] = useState('')
  const mayDrift = useCan('vault:drift')

  const load = useCallback(async () => {
    try {
      setError(null)
      const [inv, dr] = await Promise.all([
        vaultInventory(),
        mayDrift ? vaultDrift() : Promise.resolve(null),
      ])
      setInventory(inv)
      setDrift(dr)
    } catch (err) {
      setError(explain(err))
    }
  }, [mayDrift])

  useEffect(() => { void load() }, [load])

  const rows = (inventory?.fields ?? []).filter((f) =>
    (!filterClass || f.classification === filterClass)
    && (!filterProtection || f.protection === filterProtection))

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-erp-lg font-semibold">Data Vault &amp; PII</h1>
        <p className="text-erp-xs text-muted-foreground mt-0.5">
          Every field holding personal data, how it is protected, and whether the schema
          agrees.
        </p>
      </div>

      <ErrorStrip message={error} className="rounded-md border" />
      {inventory === null && !error && <LoadingBlock />}

      {inventory && (
        <>
          <div className="grid gap-2"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
            <MetricCard label="Declared fields" value={String(inventory.totalFields)} />
            <MetricCard label="Tables covered" value={String(inventory.tablesCovered)} />
            <MetricCard label="Rows holding PII"
              value={fmtNumber(inventory.totalPopulatedRows)} />
            <MetricCard label="Need consent"
              value={String(inventory.consentRequiredFields)} />
          </div>

          {/* ── the drift verdict, first ───────────────────────────────── */}
          {drift && (
            <div className={cn('rounded-md border-2 px-4 py-3',
              drift.highSeverity > 0
                ? 'border-red-300 bg-red-50 dark:border-red-500/30 dark:bg-red-500/10'
                : drift.findings > 0
                  ? 'border-amber-300 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10'
                  : 'border-emerald-300 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10')}>
              <div className="flex items-start gap-2">
                {drift.highSeverity > 0
                  ? <ShieldAlert className="h-5 w-5 shrink-0 text-red-700 dark:text-red-400" />
                  : drift.findings > 0
                    ? <AlertTriangle className="h-5 w-5 shrink-0 text-amber-700 dark:text-amber-400" />
                    : <ShieldCheck className="h-5 w-5 shrink-0 text-emerald-700 dark:text-emerald-400" />}
                <div className="flex-1">
                  <p className="text-erp-sm font-medium">
                    {drift.findings === 0
                      ? 'The schema does what the register says.'
                      : `${drift.findings} finding${drift.findings === 1 ? '' : 's'}`
                        + (drift.highSeverity > 0 ? ` — ${drift.highSeverity} serious` : '')}
                  </p>
                  <p className="text-erp-xs mt-0.5 opacity-80">
                    {drift.declaredFields} declared fields checked against
                    {' '}{drift.tablesInspected} live tables.
                  </p>
                  {drift.details.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {drift.details.map((f, i) => (
                        <li key={`${f.table}.${f.column}-${i}`} className="text-erp-xs">
                          <span className={cn('font-medium',
                            f.severity === 'HIGH' && 'text-red-700 dark:text-red-400')}>
                            {f.severity === 'HIGH' ? '● ' : '○ '}
                            {f.table}.{f.column}
                          </span>
                          {' — '}{f.detail}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          {!mayDrift && (
            <Note>
              The protection check needs <code>vault:drift</code>. It is a separate authority
              because its output names every place personal data is not protected as
              claimed — which is a map for anybody who wanted one.
            </Note>
          )}

          {/* ── filters ───────────────────────────────────────────────── */}
          <div className="flex flex-wrap items-end gap-2">
            <label className="block">
              <span className="block text-erp-xs text-muted-foreground mb-1">
                Classification
              </span>
              <select value={filterClass} onChange={(e) => setFilterClass(e.target.value)}
                className="erp-input h-9 text-erp-sm">
                <option value="">All</option>
                {Object.keys(inventory.byClassification).map((c) => (
                  <option key={c} value={c}>{c.toLowerCase()}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-erp-xs text-muted-foreground mb-1">Protection</span>
              <select value={filterProtection}
                onChange={(e) => setFilterProtection(e.target.value)}
                className="erp-input h-9 text-erp-sm">
                <option value="">All</option>
                {Object.keys(inventory.byProtection).map((p) => (
                  <option key={p} value={p}>{p.toLowerCase().replace(/_/g, ' ')}</option>
                ))}
              </select>
            </label>
            <span className="text-erp-xs text-muted-foreground pb-2">
              {rows.length} of {inventory.totalFields}
            </span>
          </div>

          {/* ── the inventory ─────────────────────────────────────────── */}
          <ErpTable head={(
            <>
              <Th>Field</Th>
              <Th>Kind</Th>
              <Th>Classification</Th>
              <Th>Protection</Th>
              <Th className="text-right">Rows</Th>
              <Th>Retention</Th>
              <Th>Consent</Th>
            </>
          )}>
            {rows.map((f) => <FieldRow key={`${f.table}.${f.column}`} field={f} />)}
          </ErpTable>

          <Note tone="info">
            Every number here is a row count. Nothing on this page carries a personal
            value — "how many voters have a phone on record" is answerable without a single
            phone number leaving the database.
          </Note>
        </>
      )}
    </div>
  )
}

const CLASS_TONE: Record<string, string> = {
  PUBLIC: 'bg-muted text-muted-foreground',
  INTERNAL: 'bg-sky-100 text-sky-900 dark:bg-sky-500/15 dark:text-sky-200',
  CONFIDENTIAL: 'bg-amber-100 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200',
  RESTRICTED: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300',
}

/** NONE is called out — an unprotected field is the point of having this column. */
const PROTECTION_TONE: Record<string, string> = {
  NONE: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300',
  GENERALISED: 'bg-sky-100 text-sky-900 dark:bg-sky-500/15 dark:text-sky-200',
  BLIND_INDEX: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
  ENCRYPT: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
  ENCRYPT_INDEXED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
  HASH: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
}

function FieldRow({ field }: { field: PiiFieldResponse }) {
  return (
    <tr>
      <Td>
        <span className="font-mono text-erp-xs">{field.table}.{field.column}</span>
        {field.note && (
          <span className="block text-erp-xs text-muted-foreground mt-0.5 max-w-[30rem]">
            {field.note}
          </span>
        )}
        {!field.verified && (
          <span className="inline-flex items-center gap-1 mt-0.5 text-erp-xs
                           text-red-700 dark:text-red-400 font-medium">
            <AlertTriangle className="h-3 w-3" /> the schema disagrees with this
          </span>
        )}
        {field.verified && (
          <span className="inline-flex items-center gap-1 mt-0.5 text-erp-xs
                           text-emerald-700 dark:text-emerald-400">
            <CheckCircle2 className="h-3 w-3" /> verified against the schema
          </span>
        )}
      </Td>
      <Td className="text-erp-xs">{field.piiType.replace(/_/g, ' ').toLowerCase()}</Td>
      <Td>
        <span className={cn('inline-flex rounded px-1.5 py-0.5 text-erp-xs font-medium',
          CLASS_TONE[field.classification])}>
          {field.classification.toLowerCase()}
        </span>
      </Td>
      <Td>
        <span className={cn('inline-flex rounded px-1.5 py-0.5 text-erp-xs font-medium',
          PROTECTION_TONE[field.protection] ?? 'bg-muted text-muted-foreground')}>
          {field.protection.toLowerCase().replace(/_/g, ' ')}
        </span>
      </Td>
      <Td className="text-right tabular-nums">
        {field.populatedRows == null ? '—' : fmtNumber(field.populatedRows)}
        {field.tableRows != null && field.tableRows > 0 && (
          <span className="block text-erp-xs text-muted-foreground">
            of {fmtNumber(field.tableRows)}
          </span>
        )}
      </Td>
      <Td className="text-erp-xs">
        {field.retentionDays == null ? 'life of record' : retention(field.retentionDays)}
      </Td>
      <Td className="text-erp-xs">
        {field.consentRequired
          ? <span className="text-amber-700 dark:text-amber-400">required</span>
          : <span className="text-muted-foreground">—</span>}
      </Td>
    </tr>
  )
}

function retention(days: number): string {
  if (days >= 365) return `${(days / 365).toFixed(1)} yr`
  if (days >= 30) return `${Math.round(days / 30)} mo`
  return `${days} d`
}
